import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from '../utils/axios';
import {
    enqueueOperation,
    getPendingOperations,
    getAllOperations,
    markOperationSynced,
    markOperationFailed,
    cancelOperation as cancelStoredOperation,
    requeueOperation,
    getPendingCount as getPendingCountFromStore,
    markRecordSynced,
    updateRecordSyncStatus,
    getStorageEstimate,
} from '../offline/offlineStorage';
import { isTokenExpired, isTokenExpiringSoon, getTokenExpiryMs } from '../offline/encryptionKey';
import { formatApiError } from '../utils/apiError';

const OfflineContext = createContext(null);

export const useOffline = () => {
    const ctx = useContext(OfflineContext);
    if (!ctx) {
        throw new Error('useOffline must be used within OfflineProvider');
    }
    return ctx;
};

const canUseNavigatorOnline = typeof window !== 'undefined' && 'navigator' in window && 'onLine' in navigator;

const getInitialOnlineStatus = () => {
    if (!canUseNavigatorOnline) return true;
    return navigator.onLine;
};

function resolveMappedId(value, idMap) {
    if (value == null) return value;
    const key = String(value);
    return idMap.has(key) ? idMap.get(key) : value;
}

function mapPayloadIds(payload, idMap) {
    const next = { ...payload };
    ['brandId', 'categoryId', 'supplierId', 'productId', 'customerId'].forEach((field) => {
        if (next[field] != null) next[field] = resolveMappedId(next[field], idMap);
    });
    if (Array.isArray(next.items)) {
        next.items = next.items.map((item) => ({
            ...item,
            productId: resolveMappedId(item.productId, idMap),
        }));
    }
    if (Array.isArray(next.allocations)) {
        next.allocations = next.allocations.map((a) => ({
            ...a,
            invoiceId: resolveMappedId(a.invoiceId, idMap),
        }));
    }
    return next;
}

export const OfflineProvider = ({ children }) => {
    const [isOnline, setIsOnline] = useState(getInitialOnlineStatus);
    const [pendingCount, setPendingCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [tokenWarning, setTokenWarning] = useState('');
    const [storageWarning, setStorageWarning] = useState('');
    const isSyncingRef = useRef(false);

    const refreshPendingCount = useCallback(async () => {
        const [count, all, storage] = await Promise.all([
            getPendingCountFromStore(),
            getAllOperations(),
            getStorageEstimate(),
        ]);
        setPendingCount(count);
        setFailedCount(all.filter((o) => o.status === 'FAILED' && !o.synced).length);
        if (storage.critical) {
            setStorageWarning('Offline storage is almost full. Sync or clear old offline data.');
        } else if (storage.warn) {
            setStorageWarning('Offline storage is over 80% full.');
        } else {
            setStorageWarning('');
        }
        if (isTokenExpired()) {
            setTokenWarning('Your session expired offline. Sign in again to sync queued work.');
        } else if (isTokenExpiringSoon()) {
            const exp = getTokenExpiryMs();
            const mins = exp ? Math.max(1, Math.round((exp - Date.now()) / 60000)) : 60;
            setTokenWarning(`Session expires in ~${mins} min. Stay online briefly to refresh before queuing more work.`);
        } else {
            setTokenWarning('');
        }
    }, []);

    const processQueue = useCallback(async () => {
        if (!getInitialOnlineStatus()) return;
        if (isSyncingRef.current) return;
        if (isTokenExpired()) {
            setTokenWarning('Your session expired offline. Sign in again to sync queued work.');
            return;
        }
        isSyncingRef.current = true;
        setIsSyncing(true);
        try {
            const storedOps = await getPendingOperations();
            const queue = [...storedOps];
            const idMap = new Map();
            let authError = false;
            let successCount = 0;
            let failCount = 0;

            while (queue.length > 0) {
                const op = queue[0];
                try {
                    const token = localStorage.getItem('token');
                    if (!token || isTokenExpired()) {
                        authError = true;
                        break;
                    }

                    if (op.recordCollection && op.localId) {
                        await updateRecordSyncStatus(op.recordCollection, op.localId, 'SYNCING');
                    }

                    let serverId = null;
                    let processedPayload = mapPayloadIds({ ...op.payload }, idMap);
                    delete processedPayload.localId;
                    delete processedPayload.syncStatus;
                    delete processedPayload._id;
                    delete processedPayload.isOffline;
                    delete processedPayload.serverId;

                    if (op.type === 'CREATE_SALE' || op.type === 'CREATE_POS_SALE' || op.type === 'CREATE_INVENTORY_SALE') {
                        const response = await axios.post('/sales', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.sale?._id;
                    } else if (op.type === 'CREATE_EXPENSE') {
                        if (processedPayload.amount) processedPayload.amount = Number(processedPayload.amount);
                        const response = await axios.post('/expense-management', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.expense?._id;
                    } else if (op.type === 'CREATE_INVOICE') {
                        const response = await axios.post('/invoices', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.invoice?._id;
                    } else if (op.type === 'CREATE_BILLING_INVOICE') {
                        const response = await axios.post('/billing/invoices', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.invoice?._id;
                    } else if (op.type === 'CREATE_CUSTOMER_INVOICE') {
                        const response = await axios.post('/customer-invoices', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.invoice?._id;
                    } else if (op.type === 'CREATE_CUSTOMER_RECEIPT' || op.type === 'CREATE_CUSTOMER_PAYMENT') {
                        const response = await axios.post('/customer-payments/receipt', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.payment?._id;
                    } else if (op.type === 'CREATE_CUSTOMER') {
                        const response = await axios.post('/customers', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.customer?._id;
                    } else if (op.type === 'CREATE_PRODUCT') {
                        if (processedPayload.costPrice) processedPayload.costPrice = Number(processedPayload.costPrice);
                        if (processedPayload.sellingPrice) processedPayload.sellingPrice = Number(processedPayload.sellingPrice);
                        if (processedPayload.currentStock) processedPayload.currentStock = Number(processedPayload.currentStock);
                        if (processedPayload.minimumStockLevel) processedPayload.minimumStockLevel = Number(processedPayload.minimumStockLevel);
                        const response = await axios.post('/products', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.product?._id;
                    } else if (op.type === 'CREATE_STOCK_MOVEMENT') {
                        const response = await axios.post('/stock-movements', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.stockMovement?._id;
                    } else if (op.type === 'CREATE_PURCHASE') {
                        const promises = (op.payload.items || []).map((item) =>
                            axios.post('/stock-movements', {
                                productId: resolveMappedId(item.productId, idMap),
                                movementType: 'Stock In',
                                quantity: item.quantity,
                                reason: 'Purchase',
                                reference: op.payload.reference || 'Purchase from Supplier',
                                notes: op.payload.notes,
                            })
                        );
                        await Promise.all(promises);
                    } else if (op.type === 'CREATE_BRAND') {
                        const response = await axios.post('/brands', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.brand?._id;
                    } else if (op.type === 'CREATE_CATEGORY') {
                        const response = await axios.post('/categories', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.category?._id;
                    } else if (op.type === 'CREATE_SUPPLIER') {
                        const response = await axios.post('/suppliers', {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = response.data.data.supplier?._id;
                    } else if (op.type === 'UPDATE_PRODUCT') {
                        let targetId = op.payload.productId || op.localId;
                        targetId = resolveMappedId(targetId, idMap);
                        if (processedPayload.costPrice) processedPayload.costPrice = Number(processedPayload.costPrice);
                        if (processedPayload.sellingPrice) processedPayload.sellingPrice = Number(processedPayload.sellingPrice);
                        if (processedPayload.currentStock) processedPayload.currentStock = Number(processedPayload.currentStock);
                        if (processedPayload.minimumStockLevel) processedPayload.minimumStockLevel = Number(processedPayload.minimumStockLevel);
                        await axios.put(`/products/${targetId}`, {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = targetId;
                    } else if (op.type === 'UPDATE_BRAND') {
                        let targetId = resolveMappedId(op.payload.brandId || op.localId, idMap);
                        await axios.put(`/brands/${targetId}`, {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = targetId;
                    } else if (op.type === 'UPDATE_CATEGORY') {
                        let targetId = resolveMappedId(op.payload.categoryId || op.localId, idMap);
                        await axios.put(`/categories/${targetId}`, {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = targetId;
                    } else if (op.type === 'UPDATE_SUPPLIER') {
                        let targetId = resolveMappedId(op.payload.supplierId || op.localId, idMap);
                        await axios.put(`/suppliers/${targetId}`, {
                            ...processedPayload,
                            clientRequestId: op.idempotencyKey,
                        });
                        serverId = targetId;
                    } else {
                        console.warn(`Unknown offline op type (skipped): ${op.type}`);
                    }

                    await markOperationSynced(op.id);
                    successCount += 1;
                    if (op.localId && serverId && op.localId !== serverId) {
                        idMap.set(String(op.localId), serverId);
                        if (op.recordCollection) {
                            await markRecordSynced(op.recordCollection, op.localId, serverId);
                        }
                    } else if (op.recordCollection && op.localId) {
                        await markRecordSynced(op.recordCollection, op.localId, serverId || op.localId);
                    }
                    queue.shift();
                } catch (err) {
                    if (err.response?.status === 401) {
                        authError = true;
                        break;
                    }
                    console.warn(`Sync operation ${op.type} failed:`, err.message);
                    failCount += 1;
                    const msg = formatApiError(err, 'Could not sync offline change');
                    if (op.recordCollection && op.localId) {
                        await updateRecordSyncStatus(op.recordCollection, op.localId, 'FAILED', { lastError: msg });
                    }
                    await markOperationFailed(op.id, msg);
                    queue.shift();
                }
            }

            if (authError) {
                setTokenWarning('Sync stopped: sign in again to upload queued offline work.');
            }
            if (typeof window !== 'undefined' && (successCount || failCount)) {
                window.dispatchEvent(new CustomEvent('tauris:offline-sync', {
                    detail: { successCount, failCount, authError },
                }));
            }
        } finally {
            isSyncingRef.current = false;
            setIsSyncing(false);
            refreshPendingCount();
        }
    }, [refreshPendingCount]);

    useEffect(() => {
        refreshPendingCount();
    }, [refreshPendingCount]);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            processQueue();
        };
        const handleOffline = () => setIsOnline(false);
        // Reconcile — some browsers fire offline spuriously or miss the online event
        const syncOnlineFlag = () => {
            if (typeof navigator === 'undefined') return;
            const online = navigator.onLine;
            setIsOnline((prev) => {
                if (prev === online) return prev;
                if (online) processQueue();
                return online;
            });
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('online', handleOnline);
            window.addEventListener('offline', handleOffline);
            window.addEventListener('focus', syncOnlineFlag);
            document.addEventListener('visibilitychange', syncOnlineFlag);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('online', handleOnline);
                window.removeEventListener('offline', handleOffline);
                window.removeEventListener('focus', syncOnlineFlag);
                document.removeEventListener('visibilitychange', syncOnlineFlag);
            }
        };
    }, [processQueue]);

    const queueOperation = useCallback(
        async ({ type, payload, recordCollection, localId, idempotencyKey }) => {
            const op = await enqueueOperation({
                type,
                payload,
                recordCollection,
                localId,
                idempotencyKey: idempotencyKey || crypto.randomUUID(),
                status: 'PENDING',
            });
            refreshPendingCount();
            return op;
        },
        [refreshPendingCount]
    );

    const retrySync = useCallback(
        async (recordCollection, localId) => {
            try {
                await updateRecordSyncStatus(recordCollection, localId, 'PENDING');
                const all = await getAllOperations();
                const match = all.find(
                    (o) => o.recordCollection === recordCollection && o.localId === localId && o.status === 'FAILED'
                );
                if (match) await requeueOperation(match.id);
                await processQueue();
            } catch (err) {
                console.error('Failed to retry sync:', err);
            }
        },
        [processQueue]
    );

    const cancelQueuedOperation = useCallback(async (operationId) => {
        await cancelStoredOperation(operationId);
        await refreshPendingCount();
    }, [refreshPendingCount]);

    const retryQueuedOperation = useCallback(async (operationId) => {
        await requeueOperation(operationId);
        await processQueue();
    }, [processQueue]);

    const listOperations = useCallback(async () => getAllOperations(), []);

    const value = useMemo(() => ({
        isOnline,
        isSyncing,
        pendingCount,
        failedCount,
        tokenWarning,
        storageWarning,
        queueOperation,
        triggerSync: processQueue,
        retrySync,
        refreshPendingCount,
        cancelQueuedOperation,
        retryQueuedOperation,
        listOperations,
    }), [
        isOnline, isSyncing, pendingCount, failedCount, tokenWarning, storageWarning,
        queueOperation, processQueue, retrySync, refreshPendingCount,
        cancelQueuedOperation, retryQueuedOperation, listOperations,
    ]);

    return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};
